/**
 * Data layer for `invoice-crud-permission-scope`. Thin typed fetch wrappers over /api/v1/invoice-crud-permission-scope.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface InvoiceCrudPermissionScopeRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListInvoiceCrudPermissionScopeResponse {
	count: number;
	items: InvoiceCrudPermissionScopeRow[];
}

export interface CreateInvoiceCrudPermissionScopeBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/invoice-crud-permission-scope";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListInvoiceCrudPermissionScopeResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListInvoiceCrudPermissionScopeResponse>(res);
}

export async function create(body: CreateInvoiceCrudPermissionScopeBody): Promise<InvoiceCrudPermissionScopeRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<InvoiceCrudPermissionScopeRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const invoiceCrudPermissionScopeQueryKeys = {
	all: ["invoice-crud-permission-scope"] as const,
	list: () => [...invoiceCrudPermissionScopeQueryKeys.all] as const,
	detail: (id: string) => [...invoiceCrudPermissionScopeQueryKeys.all, id] as const,
};
